/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Spin,
  Form,
  Card,
  Button,
  DatePicker,
} from 'antd';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Table from 'components/Table';
import Uploader from 'components/Uploader';
import Select from 'components/Select';
import {
  deletePeriodoFiscal,
  getPeriodosFiscales,
  patchActualizarEstatusPeriodo,
  patchPeriodoFiscal,
  postPeriodoFiscal,
} from 'api/configuraciones/periodosFiscales';
import { toMoment } from 'utils/normalizers';
import { onError, onSuccess } from 'utils/handlers';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';
import ModalConfirmation from 'components/ModalConfirmation';
import { checkToken } from 'api/utils';
import { getEstadosGlobales } from 'api/catalogos';
import { formatReceived } from 'utils/formatValues';

export const periodosFiscalesPermission = {
  permissionModel: 'periodofiscal',
};

// eslint-disable-next-line react/prop-types
const PeriodoFiscal = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [disabledVigente, setDisabledVigente] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [disableEditar, setDisableEditar] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [visibleInactivo, setVisibleInactivo] = useState(false);
  const [estadosGlobales, setEstadosGlobales] = useState([]);
  const [visibleVigente, setVisibleVigente] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getPeriodosFiscales();
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _data,
        _estadosGlobales,
      ] = await Promise.all([
        getPeriodosFiscales(),
        getEstadosGlobales(),
      ]);
      if (mounted) {
        setData(formatReceived(_data));
        setEstadosGlobales(_estadosGlobales);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setDisabledVigente(false);
    setDisabled(false);
    setDisableEditar(false);
    setVisibleInactivo(false);
    form.resetFields();
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key ? patchPeriodoFiscal(key, values) : postPeriodoFiscal(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deletePeriodoFiscal(key);
        if (deleted) {
          onCancel();
          setVisibleAlert(false);
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = () => {
    setVisible(true);
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    const currYear = toMoment(new Date()).format('YYYY');
    setDisabledVigente(match.periodo.toString() !== currYear);
    if (match?.estados_globales !== ESTATUS_ACTIVO) {
      setDisableEditar(true);
    } else {
      setDisableEditar(false);
    }
    form.setFieldsValue({
      periodo: toMoment(match.periodo, ['YYYY']),
    });
    setLoading(false);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un Periodo ya autorizado');
      }
      const res = await patchActualizarEstatusPeriodo(key, ESTATUS_AUTORIZADO);
      if (res) {
        onSuccess(res);
        setVisibleAutorizar(false);
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationEstatus = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar un Periodo ya cancelado');
      }
      const res = await patchActualizarEstatusPeriodo(key, ESTATUS_CANCELADO);
      if (res) {
        onSuccess(res);
        setVisibleCancelar(false);
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Periodo',
      dataIndex: 'periodo',
      key: 'periodo',
      width: 100,
    },
    {
      titleText: 'Fecha Inicial',
      dataIndex: 'fecha_inicial',
      key: 'fecha_inicial',
      width: 150,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Fecha Final',
      dataIndex: 'fecha_final',
      key: 'fecha_final',
      width: 150,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Fecha Autorización',
      dataIndex: 'fecha_autorizacion',
      key: 'fecha_autorizacion',
      width: 180,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Vigente',
      dataIndex: 'vigente',
      key: 'vigente',
      width: 100,
      render: (val) => (val ? 'Si' : 'No'),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
    fecha_autorizacion: [
      { required: true },
      {
        validator: async () => {
          const { fecha_inicial, fecha_autorizacion } = form.getFieldsValue();
          if (fecha_inicial && fecha_autorizacion
            && fecha_inicial.diff(fecha_autorizacion, 'days') <= 0) {
            throw new Error('Este campo debe ser menor que la Fecha Inicial');
          }
        },
      },
    ],
  };

  const setDates = (val) => {
    const year = val?.format('YYYY');
    if (year) {
      const currYear = toMoment(new Date()).format('YYYY');
      form.setFieldsValue({
        fecha_inicial: toMoment(`${year}-01-01`),
        fecha_final: toMoment(`${year}-12-31`),
        fecha_autorizacion: toMoment(`${year - 1}-12-31`),
      });
      const { vigente } = form.getFieldsValue();
      if (vigente && year !== currYear) {
        form.setFieldsValue({ vigente: false });
      }
      setDisabledVigente(year !== currYear);
    } else {
      form.resetFields([
        'fecha_inicial',
        'fecha_final',
        'fecha_autorizacion',
      ]);
    }
  };

  const getDisplayName = () => {
    const [key] = selectedRowKeys;
    const { periodo } = key
      ? data.find((e) => e.id === key) : form.getFieldsValue();
    return `${periodo?.format?.('YYYY') || periodo}`;
  };

  return (
    <Row align="center" justify="center" className="container">
      <Col span={24}>
        <Spin tip="Cargando..." spinning={loading}>
          {!open ? (
            <Table
              cols={columns}
              data={data}
              handleOnRowClick={handleOnRowClick}
              rowSelection={rowSelection}
              controls={{
                onClickAdd,
                onClickEdit: () => onClickEdit(),
                onClickDelete,
              }}
              loading={loading}
              disableDelete={
                data?.find((e) => e?.id === selectedRowKeys[0])?.vigente === true
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
                || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
              }
              customActions={[
                {
                  onClick: () => setVisibleAutorizar(true),
                  permission: { model: 'periodofiscal', action: 'autorizar' },
                  text: 'Autorizar',
                  icon: <CheckCircleOutlined />,
                  disabledBySelection: false,
                  className: 'success',
                  disabled: !selectedRowKeys?.length
                    || data?.find((e) => e?.id === selectedRowKeys[0]?.vigente === true)
                    || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
                    || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
                    || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_INACTIVO,
                },
                {
                  permission: { model: 'periodofiscal', action: 'cancelar' },
                  onClick: () => setVisibleCancelar(true),
                  text: 'Cancelar',
                  icon: <CloseCircleOutlined />,
                  disabledBySelection: false,
                  className: 'cancel',
                  disabled: !selectedRowKeys?.length
                    || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO,
                },
              ]}
              permission={permission}
              allowExportPDF={false}
            />
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Ejercicio`}
                extra={(
                  <FormSubmitControls
                    onFinish={() => form.submit()}
                    onCancel={onCancel}
                  />
                )}
                bordered={false}
              >
                <Form
                  layout="vertical"
                  form={form}
                  onFinish={onFinish}
                  scrollToFirstError
                  initialValues={{ vigente: false }}
                >
                  <Row gutter={10}>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="periodo"
                        label="Periodo"
                        rules={rules.required}
                        hasFeedback
                      >
                        <DatePicker
                          disabled={selectedRowKeys.length}
                          onChange={setDates}
                          picker="year"
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="fecha_inicial"
                        label="Fecha inicial"
                        rules={rules.required}
                        hasFeedback
                      >
                        <DatePicker
                          placeholder=""
                          format="DD/MM/YYYY"
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="fecha_final"
                        label="Fecha final"
                        rules={rules.required}
                        hasFeedback
                      >
                        <DatePicker
                          placeholder=""
                          format="DD/MM/YYYY"
                          disabled
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="fecha_autorizacion"
                        label="Fecha de autorización"
                        rules={rules.fecha_autorizacion}
                        hasFeedback
                      >
                        <DatePicker
                          placeholder=""
                          format="DD/MM/YYYY"
                          disabled={disableEditar}
                          disabledDate={(d) => parseInt(d.format('YYYY'), 10)
                            !== parseInt(form.getFieldValue('periodo')?.format('YYYY'), 10) - 1}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="vigente"
                        label="Vigente"
                        hasFeedback
                        rules={rules.required}
                      >
                        <Select
                          disabled={disableEditar || disabledVigente}
                          trueFalse
                          onChange={(val) => {
                            if (val) {
                              setVisibleVigente(true);
                            }
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      xs={24}
                      sm={24}
                      md={8}
                    >
                      <Form.Item
                        name="estados_globales"
                        label="Estatus"
                        initialValue={ESTATUS_ACTIVO}
                      >
                        <Select
                          dataSource={estadosGlobales}
                          disabled={!selectedRowKeys || disableEditar}
                          activeInactive={!disableEditar}
                          onChange={(val) => setVisibleInactivo(val === ESTATUS_INACTIVO)}
                        />
                      </Form.Item>
                    </Col>
                    <Uploader
                      title="Documento Anexo PDF"
                      limitMB={50}
                      file={form.getFieldValue('documento_anexo_PDF')}
                      formItemName="documento_anexo_PDF"
                      allowedExtensions={['pdf']}
                      disabled={disabled || disableEditar}
                    />
                    <Uploader
                      title="Documento Anexo Word"
                      limitMB={50}
                      file={form.getFieldValue('documento_anexo_word')}
                      formItemName="documento_anexo_word"
                      allowedExtensions={['doc', 'docx']}
                      disabled={disabled || disableEditar}
                    />
                  </Row>
                  <Form.Item hidden>
                    <Button htmlType="submit" />
                  </Form.Item>
                </Form>
              </Card>
            </Col>
          )}
          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => {
              setVisibleAlert(false);
              setSelectedRowKeys([]);
            }}
            open={visibleAlert}
            content={`Ejercicio ${getDisplayName()}`}
          />
          <ModalConfirmation
            onFinish={handleOnFinishConfirmation}
            ConfirmAutorize
            onCancel={() => {
              setVisibleAutorizar(false);
            }}
            open={visibleAutorizar}
            contentTitle={`Autorizar el Ejercicio ${getDisplayName()}`}
            loading={loading}
          />
          <ModalConfirmation
            onFinish={handleOnFinishConfirmationEstatus}
            onFinishText="Continuar"
            onCancel={() => {
              setVisibleCancelar(false);
            }}
            open={visibleCancelar}
            title="Confirmación"
            contentTitle={`Cancelar el Ejercicio ${getDisplayName()}`}
            subMessage="Esta acción no podrá revertirse"
            loading={loading}
          />
          <ModalConfirmation
            onFinish={() => setVisibleInactivo(false)}
            ConfirmInactive
            onCancel={() => {
              form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
              setVisibleInactivo(false);
            }}
            contentTitle={`Inactivar el Periodo ${getDisplayName()}`}
            open={visibleInactivo}
            loading={loading}
          />
          <ModalConfirmation
            onFinish={() => setVisibleVigente(false)}
            title="Confirmación"
            onCancel={() => {
              form.setFieldValue('vigente', false);
              setVisibleVigente(false);
            }}
            contentTitle={`Registrar el Periodo ${getDisplayName()} como Vigente`}
            open={visibleVigente}
            loading={loading}
          />
        </Spin>
      </Col>
    </Row>
  );
};

export default PeriodoFiscal;
